/**
 * Gig Utility Functions
 * Helper functions for managing gig expiration and filtering
 */

/**
 * Normalize any common gig time string to 24h "HH:MM".
 * - Strips seconds / fractional parts from HTML time inputs.
 * - Repairs mistaken "double PM" storage (e.g. 19 treated as 19+12 → 31) by subtracting 12 once when hour is 24–47.
 * - Parses trailing 12h "7:00 PM" style when no 24h match.
 */
export const normalizeTime24 = (raw) => {
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  if (!s) return '';

  const ampm12 = s.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])\s*$/);
  if (ampm12) {
    let hh = parseInt(ampm12[1], 10);
    const mm = parseInt(ampm12[2], 10);
    const ap = ampm12[3].toUpperCase();
    if (Number.isNaN(hh) || Number.isNaN(mm)) return '';
    if (ap === 'PM' && hh < 12) hh += 12;
    if (ap === 'AM' && hh === 12) hh = 0;
    hh = Math.min(23, Math.max(0, hh));
    const m = Math.min(59, Math.max(0, mm));
    return `${String(hh).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  const firstColon = s.indexOf(':');
  if (firstColon === -1) return '';
  const hPart = s.slice(0, firstColon).trim();
  const afterColon = s.slice(firstColon + 1);
  const minMatch = afterColon.match(/^(\d{1,2})/);
  if (!minMatch) return '';
  let h = parseInt(hPart, 10);
  let m = parseInt(minMatch[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';

  // Bugfix: values like 31:00 (7 PM stored as 19+12)
  if (h > 23 && h <= 47) {
    h -= 12;
  }
  if (h > 23) {
    h = h % 24;
  }
  m = Math.min(59, Math.max(0, m));
  h = Math.min(23, Math.max(0, h));
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/**
 * Display label: 24h or any raw stored value → "h:mm AM/PM"
 */
export const formatGigTime12h = (raw) => {
  const t = normalizeTime24(raw);
  if (!t) return '';
  const [hours, minutes] = t.split(':');
  const hour = parseInt(hours, 10);
  if (Number.isNaN(hour)) return String(raw).trim();
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

/**
 * Check if a gig has expired (after midnight on the gig date, locally)
 * A gig expires when the current local date is AFTER the gig date
 * Gigs show on their date but hide after midnight passes (next day)
 * 
 * @param {string|Date} gigDate - The gig date (YYYY-MM-DD format string or Date object)
 * @returns {boolean} - true if the gig has expired (should be hidden), false if still valid
 */
export const isGigExpired = (gigDate) => {
  if (!gigDate) return true; // No date = expired
  
  try {
    // Get today's date at midnight (local time)
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    todayLocal.setHours(0, 0, 0, 0);
    
    // Parse gig date - handle different formats
    let gigDateObj;
    if (typeof gigDate === 'string') {
      // If YYYY-MM-DD format, parse it to local date
      if (gigDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = gigDate.split('-').map(Number);
        gigDateObj = new Date(year, month - 1, day); // month is 0-indexed
      } else {
        gigDateObj = new Date(gigDate);
      }
    } else if (gigDate instanceof Date) {
      gigDateObj = gigDate;
    } else {
      gigDateObj = new Date(gigDate);
    }
    
    // Get gig date at midnight (local time)
    const gigDateLocal = new Date(gigDateObj.getFullYear(), gigDateObj.getMonth(), gigDateObj.getDate());
    gigDateLocal.setHours(0, 0, 0, 0);
    
    // Gig is expired if today's local date is AFTER the gig date
    // This means once midnight passes on the gig date, it's expired
    // Gig shows on the gig date itself (today === gigDate is NOT expired)
    return todayLocal > gigDateLocal;
  } catch (error) {
    console.error('Error checking gig expiration:', error, gigDate);
    return true; // On error, assume expired to be safe
  }
};

/**
 * Filter out expired gigs from an array
 * 
 * @param {Array} gigs - Array of gig objects with a 'date' property
 * @returns {Array} - Array of non-expired gigs
 */
export const filterExpiredGigs = (gigs) => {
  if (!Array.isArray(gigs)) return [];
  return gigs.filter(gig => !isGigExpired(gig.date));
};

/**
 * Check if a gig is upcoming (not expired and in the future)
 * 
 * @param {string|Date} gigDate - The gig date
 * @returns {boolean} - true if the gig is upcoming
 */
export const isGigUpcoming = (gigDate) => {
  return !isGigExpired(gigDate);
};

